import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import Button from '../Button/Button';
import { neo4jService } from '../../services/neo4jService';
import { useNavigate } from 'react-router-dom';
import './HeroSection.css';

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loadSlides = async () => {
      try {
        const fetchedSlides = await neo4jService.getHeroSlides();
        setSlides(fetchedSlides);
        setError(null);
      } catch (err) {
        console.error('Error loading slides:', err);
        setError('Failed to load slides');
      } finally {
        setLoading(false);
      }
    };
    
    loadSlides();
  }, []);

  const handleNextSlide = useCallback(() => {
    if (!isAnimating && slides.length > 0) {
      setIsAnimating(true);
      setCurrentSlide((prev) => (prev + 1) % slides.length);
      setTimeout(() => setIsAnimating(false), 500);
    }
  }, [isAnimating, slides.length]);

  const handlePrevSlide = useCallback(() => {
    if (!isAnimating && slides.length > 0) {
      setIsAnimating(true);
      const newSlide = currentSlide === 0 ? slides.length - 1 : currentSlide - 1;
      setCurrentSlide(newSlide);
      setTimeout(() => setIsAnimating(false), 500);
    }
  }, [isAnimating, currentSlide, slides.length]);

  useEffect(() => {
    const interval = setInterval(handleNextSlide, 5000);
    return () => clearInterval(interval);
  }, [handleNextSlide]);

  const handleButtonClick = (slide) => {
    const categorySlug = slide.category
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-');

    navigate(`/category/${encodeURIComponent(categorySlug)}`);
  };

  if (loading) {
    return (
      <section className="hero-section">
        <div className="hero-loader">Loading...</div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="hero-section">
        <div className="hero-error">{error}</div>
      </section>
    );
  }

  if (!slides || slides.length === 0) {
    return null;
  }

  const currentSlideData = slides[currentSlide];
  const slideStyle = {
    '--slide-color': currentSlideData.color
  };

  return (
    <section className="hero-section" style={slideStyle}>
      <div className="hero-slider">
        {slides.map((slide, index) => (
          <div 
            key={slide.id}
            className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
            style={{
              transform: `translateX(${(index - currentSlide) * 100}%)`
            }}
          >
            <div className="hero-content">
              <h1 className="hero-title">{slide.title}</h1>
              <h2 className="hero-subtitle">{slide.subtitle}</h2>
              <p className="hero-description">{slide.description}</p>
              <Button 
                onClick={() => handleButtonClick(slide)}
                className="hero-button"
              >
                {slide.buttonText}
              </Button>
            </div>
            <div className="hero-image-container">
              <img 
                src={slide.image} 
                alt={slide.title} 
                className="hero-image"
              />
              <div className="hero-overlay" />
            </div>
          </div>
        ))}
      </div>

      <div className="hero-controls">
        <button 
          className="hero-arrow hero-arrow-left"
          onClick={handlePrevSlide}
          disabled={isAnimating}
        >
          <ChevronLeft size={24} />
        </button>

        <div className="hero-dots">
          {slides.map((_, index) => (
            <button
              key={index}
              className={`hero-dot ${index === currentSlide ? 'active' : ''}`}
              onClick={() => {
                if (!isAnimating && index !== currentSlide) {
                  setIsAnimating(true);
                  setCurrentSlide(index);
                  setTimeout(() => setIsAnimating(false), 500);
                }
              }}
              disabled={isAnimating}
            />
          ))}
        </div>

        <button 
          className="hero-arrow hero-arrow-right"
          onClick={handleNextSlide}
          disabled={isAnimating}
        >
          <ChevronRight size={24} />
        </button>
      </div>

      <div className="hero-progress">
        <div 
          className="hero-progress-bar" 
          style={{
            width: `${((currentSlide + 1) / slides.length) * 100}%`,
            backgroundColor: slides[currentSlide].color
          }}
        />
      </div>
    </section>
  );
};

export default HeroSection;
